import {
    all,
    call,
    put,
    select,
    takeLatest,
    delay,
    race,
} from "redux-saga/effects";
import { chunk } from "lodash";
import db from "../../common/lib/database";
import { enums, http } from "../../common/lib";
import config from "../../config";
import { get, update } from "idb-keyval";
import actions from "../actions";
import { retryWithBackoff } from "../../common/lib/utils";
import { actionCreators, actionTypes } from "./actions";
import sagaUtils from "./sagaUtils.js";
import { logToSentry } from "../../config/Sentry";

const BATCH_SIZE = 5;
const SMALL_BATCH_SIZE = 1;
const SUCCESS_STATUS = 201;
const MAX_RETRIES = 3;
const TIMEOUT_DURATION = 5000; // 25 seconds

/** TODO 
    1. Reduce duplicates 
    2. Ensure we have one function submit & partial submit
    3. Review the data in the store and remove if not necessary
**/

function* retrySaga(fn, args = [], maxRetries = 3, retryDelay = 2000) {
    let attempt = 0;
    while (attempt < maxRetries) {
        try {
            return yield call(fn, ...args); // Execute function with arguments
        } catch (error) {
            attempt++;
            console.warn(
                `${fn.name} Attempt ${attempt}/${maxRetries} failed:`,
                error
            );

            if (attempt === maxRetries) {
                console.error(
                    `Max retries reached for ${fn.name}. Aborting...`
                );
                logToSentry(`Max retries reached. Aborting...`, {
                    function: fn.name,
                    args,
                    error,
                });
                throw error; // Throw error after max retries
            }

            yield call(sagaUtils.delayFn, retryDelay, attempt, 15000); // 15s max or make it configurable
        }
    }
}

function* submitAllAuditAssignments(auditId, auditUpload) {
    const auditSignature = auditUpload[auditId]
        ? auditUpload[auditId].auditSignature
        : "";

    // /** To map assetAuditor we need auditAssignment  */
    const auditAssignmentDB = (yield call(get, db.types.auditAssignment)) || [];
    const auditAssignments = db.queries.forAuditLocationAudit(
        auditId,
        auditAssignmentDB
    );

    for (let auditAssignment of auditAssignments) {
        yield call(http.authorized.post, config.api.paths.auditSubmissions, {
            isSync: 0,
            auditSignature: {
                ...auditSignature,
            },
            createdDate: new Date().toISOString(),
            firstAssetChecked: null,
            audit: auditId,
            auditAssetContent: [],
            auditAssignment: auditAssignment.id,
        });
    }
}

/** Helper function to process batch submissions with retry logic */
function* processBatch(
    items,
    mapFunction,
    auditId,
    auditSignature,
    firstAssetChecked,
    type,
    auditAssignmentId = null
) {
    if (items.length === 0) return;

    for (let batch of chunk(items, BATCH_SIZE)) {
        let attempts = 0;
        let success = false;

        while (attempts < MAX_RETRIES && !success) {
            try {
                attempts++;

                const content = yield all(
                    batch.map((item) => call(mapFunction, item))
                );

                const requestData = {
                    isSync: 0,
                    createdDate: new Date().toISOString(),
                    auditSignature: auditSignature
                        ? { ...auditSignature }
                        : undefined,
                    firstAssetChecked,
                    audit: auditId,
                };

                if (type === "altAsset") {
                    requestData.altAssetContent = content;
                } else {
                    requestData.auditAssetContent = content;
                    requestData.auditAssignment = auditAssignmentId;
                }

                const response = yield call(
                    http.authorized.post,
                    config.api.paths.auditSubmissions,
                    requestData
                );

                if (!response || response.status >= 400) {
                    throw new Error(
                        `${type} submission failed with status: ${response?.status}`
                    );
                }

                yield put(actionCreators.statusUploaded(batch.length, false));
                success = true;
            } catch (error) {
                console.error(
                    `Attempt ${attempts} failed for ${type} batch in audit ID ${auditId}, AuditAssignment ID: ${
                        auditAssignmentId || "N/A"
                    }:`,
                    error
                );
                yield put(actionCreators.statusUploaded(batch.length, false));
                logToSentry(
                    `Attempt ${attempts} failed for ${type} batch in audit ID ${auditId}, AuditAssignment ID: ${
                        auditAssignmentId || "N/A"
                    }:`,
                    { error }
                );

                if (attempts >= MAX_RETRIES) {
                    console.error(
                        `All ${MAX_RETRIES} attempts failed for ${type} batch. Throwing error.`
                    );
                    throw error;
                }

                yield call(sagaUtils.delayFn, 2000, attempts, 15000); // or make base + max configurable
            }
        }
    }
}

function* groupAuditSubmissionClear(action) {
    const { auditId, clearGroupData } = action.payload;

    const { auditUpload, audits } = yield select((state) => state);
    const { cleanAllGroupData, cleanAllData } = sagaUtils;

    const currentAudit = audits?.data?.find((item) => item.id === auditId);

    try {
        yield call(
            retrySaga,
            submitAllAuditAssignments,
            [auditId, auditUpload],
            3,
            2000
        );
    } catch (error) {
        console.error(`Final failure for audit ID ${auditId}:`, error);
        logToSentry(`Final failure for audit ID ${auditId}:`, { error });
        yield put(
            actions.toast.create.error(`Final failure for audit ID ${auditId}:`)
        );
        return; // Stop execution if retries fail
    }

    if (currentAudit) {
        if (clearGroupData) yield call(cleanAllGroupData, currentAudit);
        else yield call(cleanAllData, currentAudit);
    }
}

function* partialSubmit(action) {
    const { remainingCount, auditId } = action.payload;
    const SIZE =
        remainingCount && remainingCount <= 10 ? SMALL_BATCH_SIZE : BATCH_SIZE;

    try {
        const { mapAltAsset, mapAssetAuditor } = sagaUtils;

        yield put(actionCreators.partialUploaded(0, true));

        // Get initial state data
        const { auditUpload, audits } = yield select((state) => state);
        const firstAssetChecked =
            auditUpload[auditId]?.firstAssetChecked || null;

        // ==================== ALT ASSETS SUBMISSION ====================
        const altAssetsDB = (yield call(get, db.types.altAsset)) || [];
        const altAssets = db.queries.forAudit(auditId, altAssetsDB);
        const altAssetsToSubmit = altAssets.filter((item) =>
            Boolean(item.uuid)
        );

        const submitAltAsset = altAssetsToSubmit.filter(
            (item) => !item.assetSubmitted && item.audit.id === auditId
        );
        // console.log("altAssetsToSubmit", altAssetsToSubmit);
        // console.log("submitAltAsset", submitAltAsset);
        let altAssetStatus;
        let altAssetsSubmitted = 0;

        if (submitAltAsset.length > 0) {
            // console.log(
            //     `[PartialSubmit] Found ${submitAltAsset.length} alt assets to submit`
            // );

            for (let [batchIndex, batch] of chunk(
                submitAltAsset,
                SIZE
            ).entries()) {
                let attempts = 0;
                let success = false;
                const batchId = `${auditId}-alt-${batchIndex}`;

                while (attempts < MAX_RETRIES && !success) {
                    try {
                        attempts++;
                        // console.log(
                        //     `[PartialSubmit] Processing alt asset batch ${batchId} (attempt ${attempts})`
                        // );

                        // Prepare batch content
                        const content = yield all(
                            batch.map((item) => call(mapAltAsset, item))
                        );

                        // Create timeout race condition
                        const apiCall = http.authorized.post(
                            config.api.paths.auditSubmissions,
                            {
                                createdDate: new Date().toISOString(),
                                isSync: 1,
                                firstAssetChecked: firstAssetChecked,
                                altAssetContent: content,
                                audit: auditId,
                            }
                        );

                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(
                                () =>
                                    reject(
                                        new Error(
                                            `Timeout after ${
                                                TIMEOUT_DURATION / 1000
                                            }s`
                                        )
                                    ),
                                TIMEOUT_DURATION
                            )
                        );

                        const response = yield race({
                            api: call(() => apiCall),
                            timeout: call(() => timeoutPromise),
                        });

                        if (response.timeout) {
                            console.warn(
                                `[PartialSubmit] Timeout for alt asset batch ${batchId}`
                            );
                            logToSentry("Batch Timeout", {
                                type: "alt-assets",
                                batchId,
                                auditId,
                                attempt: attempts,
                            });
                            break; // Skip to next batch
                        }

                        const apiResponse = response.api;
                        if (!apiResponse || apiResponse.status >= 400) {
                            throw new Error(
                                `Alt asset submission failed with status: ${apiResponse?.status}`
                            );
                        }

                        altAssetStatus = apiResponse.status;
                        altAssetsSubmitted += batch.length;

                        // console.log(
                        //     `[PartialSubmit] Successfully submitted alt asset batch ${batchId}`
                        // );
                        yield put(
                            actionCreators.partialUploaded(batch.length, true)
                        );

                        // Update DB on success
                        if (SUCCESS_STATUS === altAssetStatus) {
                            yield call(
                                update,
                                db.types.altAsset,
                                (updatedAltAssetDB = []) => {
                                    if (!updatedAltAssetDB)
                                        updatedAltAssetDB = [];
                                    const updatedAssets = updatedAltAssetDB.map(
                                        (obj) => {
                                            if (
                                                !obj.assetSubmitted &&
                                                obj.audit.id === auditId
                                            ) {
                                                obj.assetSubmitted = true;
                                                obj.auditImages = [];
                                            }
                                            return obj;
                                        }
                                    );
                                    return updatedAssets;
                                }
                            );
                        }

                        success = true;
                    } catch (error) {
                        console.error(
                            `[PartialSubmit] Alt asset batch ${batchId} failed:`,
                            error.message
                        );
                        logToSentry("Alt Asset Batch Error", {
                            error: error.message,
                            batchId,
                            auditId,
                            attempt: attempts,
                        });

                        if (
                            error.message.includes("Timeout") ||
                            attempts >= MAX_RETRIES
                        ) {
                            console.warn(
                                `[PartialSubmit] Giving up  after  ${MAX_RETRIES} tries on alt asset batch ${batchId}`
                            );
                            break;
                        }

                        yield call(sagaUtils.delayFn, 2000 * attempts); // Exponential backoff
                    }
                }
            }
        }

        // ==================== ASSET AUDITORS SUBMISSION ====================
        const assetAuditorsDB = (yield call(get, db.types.assetAuditor)) || [];
        const submitAsset = assetAuditorsDB.filter(
            (item) =>
                !item.assetSubmitted &&
                item.auditAssignment.auditLocation.audit.id === auditId
        );

        const totalAssetsToSubmit = submitAsset.length;
        let submittedCount = 0;
        let auditAssetStatus;

        // Get audit assignments
        const auditAssignmentDB =
            (yield call(get, db.types.auditAssignment)) || [];
        const auditAssignments = db.queries.forAuditLocationAudit(
            auditId,
            auditAssignmentDB
        );

        // console.log(
        //     `[PartialSubmit] Found ${totalAssetsToSubmit} assets to submit across ${auditAssignments.length} assignments`
        // );

        for (let [
            assignmentIndex,
            auditAssignment,
        ] of auditAssignments.entries()) {
            const assetAuditors = db.queries.forAuditAssignments(
                [auditAssignment.id],
                submitAsset
            );

            for (let [batchIndex, batch] of chunk(
                assetAuditors,
                SIZE
            ).entries()) {
                let attempts = 0;
                let success = false;
                const batchId = `${auditId}-auditor-${batchIndex}`;

                while (attempts < MAX_RETRIES && !success) {
                    try {
                        attempts++;
                        // console.log(
                        //     `[PartialSubmit] Processing asset auditor batch ${batchId} (attempt ${attempts})`
                        // );

                        // Prepare batch content
                        const content = yield all(
                            batch.map((item) => call(mapAssetAuditor, item))
                        );

                        // Create timeout race condition
                        const apiCall = http.authorized.post(
                            config.api.paths.auditSubmissions,
                            {
                                isSync: 1,
                                createdDate: new Date().toISOString(),
                                firstAssetChecked: firstAssetChecked,
                                audit: auditId,
                                auditAssetContent: content,
                                auditAssignment: auditAssignment.id,
                            }
                        );

                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(
                                () =>
                                    reject(
                                        new Error(
                                            `Timeout after ${
                                                TIMEOUT_DURATION / 1000
                                            }s`
                                        )
                                    ),
                                TIMEOUT_DURATION
                            )
                        );

                        const response = yield race({
                            api: call(() => apiCall),
                            timeout: call(() => timeoutPromise),
                        });

                        if (response.timeout) {
                            console.warn(
                                `[PartialSubmit] Timeout for asset auditor batch ${batchId}`
                            );
                            logToSentry("Batch Timeout", {
                                type: "asset-auditor",
                                batchId,
                                auditId,
                                assignmentId: auditAssignment.id,
                                attempt: attempts,
                            });
                            break;
                        }

                        const apiResponse = response.api;
                        if (!apiResponse || apiResponse.status >= 400) {
                            throw new Error(
                                `Audit asset partial submission failed with status: ${apiResponse?.status}`
                            );
                        }

                        auditAssetStatus = apiResponse.status;
                        submittedCount += batch.length;

                        // console.log(
                        //     `[PartialSubmit] Successfully submitted asset auditor batch ${batchId}`
                        // );
                        yield put(
                            actionCreators.syncUploaded(
                                submittedCount,
                                totalAssetsToSubmit,
                                true
                            )
                        );

                        success = true;
                    } catch (error) {
                        console.error(
                            `[PartialSubmit] Asset auditor batch ${batchId} failed:`,
                            error.message
                        );
                        logToSentry(
                            "[PartialSubmit] Asset Auditor Batch Error",
                            {
                                error: error.message,
                                batchId,
                                auditId,
                                assignmentId: auditAssignment.id,
                                attempt: attempts,
                            }
                        );

                        if (
                            error.message.includes("Timeout") ||
                            attempts >= MAX_RETRIES
                        ) {
                            console.warn(
                                `[PartialSubmit] Giving up on asset auditor batch ${batchId}`
                            );
                            break;
                        }

                        yield call(sagaUtils.delayFn, 2000 * attempts);
                    }
                }
            }
        }

        // Final database updates
        if (auditAssetStatus === SUCCESS_STATUS) {
            yield call(
                update,
                db.types.assetAuditor,
                (updatedAssetAuditorDB = []) => {
                    const updatedAssets = updatedAssetAuditorDB.map((obj) => {
                        if (
                            !obj.assetSubmitted &&
                            obj.auditAssignment.auditLocation.audit.id ===
                                auditId
                        ) {
                            obj.assetSubmitted = true;
                            obj.auditImages = [];
                        }
                        return obj;
                    });
                    return updatedAssets;
                }
            );
        }

        // Completion handling
        // console.log(
        //     `[PartialSubmit] Submission completed for audit ${auditId}`
        // );
        yield put(actionCreators.partialUploaded(0, false));
        yield put(actionCreators.partialSubmitSuccess());
    } catch (error) {
        console.error(
            `[PartialSubmit] Critical error for audit ${auditId}:`,
            error
        );
        const errStr = error.toString();
        logToSentry("Partial Submit Failure", {
            error: errStr,
            auditId,
            actionPayload: action.payload,
        });

        yield put(actionCreators.partialUploaded(0, false));
        yield put(actionCreators.partialSubmitFailure(errStr));
    }
}

function* post() {
    try {
        yield put(actionCreators.statusInit());
        /** Mention status it is uploading */
        yield put(actionCreators.statusUploaded(0, true));

        const { auditUpload, audits } = yield select((state) => state);
        const auditSignature = auditUpload[audits.item.id]
            ? auditUpload[audits.item.id].auditSignature
            : "";
        const firstAssetChecked = auditUpload[audits.item.id]
            ? auditUpload[audits.item.id].firstAssetChecked
            : null;

        const { cleanAllData, mapAltAsset, mapAssetAuditor } = sagaUtils;
        const altAssetsDB = (yield call(get, db.types.altAsset)) || [];
        const altAssets = db.queries.forAudit(audits.item.id, altAssetsDB);
        const altAssetsToSubmit = altAssets.filter((item) =>
            Boolean(item.uuid)
        );

        yield call(
            processBatch,
            altAssetsToSubmit,
            mapAltAsset,
            audits.item.id,
            auditSignature,
            firstAssetChecked,
            "altAsset"
        );

        // /** To map assetAuditor we need auditAssignment  */
        const auditAssignmentDB =
            (yield call(get, db.types.auditAssignment)) || [];
        const auditAssignments = db.queries.forAuditLocationAudit(
            audits.item.id,
            auditAssignmentDB
        );

        // /** Retriving the asset auditor Database  */
        const assetAuditorsDB = (yield call(get, db.types.assetAuditor)) || [];
        for (let auditAssignment of auditAssignments) {
            //     /** Filtering required data from assetAuditor */
            const assetAuditors = db.queries.forAuditAssignments(
                [auditAssignment.id],
                assetAuditorsDB
            );

            if (assetAuditors.length === 0) {
                yield call(
                    http.authorized.post,
                    config.api.paths.auditSubmissions,
                    {
                        isSync: 0,
                        auditSignature: {
                            ...auditSignature,
                        },
                        createdDate: new Date().toISOString(),
                        firstAssetChecked: null,
                        audit: audits.item.id,
                        auditAssetContent: [],
                        auditAssignment: auditAssignment.id,
                    }
                );
            } else
                yield call(
                    processBatch,
                    assetAuditors,
                    mapAssetAuditor,
                    audits.item.id,
                    auditSignature,
                    firstAssetChecked,
                    "assetAuditor",
                    auditAssignment.id
                );

            /** Audit Assignment status to be submitted */
            yield call(
                http.authorized.post,
                config.api.paths.auditAssignmentBulkUpdate,
                [
                    {
                        id: auditAssignment.id,
                        status: enums.auditAssignment.submitted,
                    },
                ]
            );
        }

        // Clearing the data and post success message and clearing current audit

        /** Remove object from the auditUpload signature */

        yield put(actionCreators.statusUploaded(0, false));
        yield call(cleanAllData, audits.item);
        yield put(actionCreators.postSuccess());
    } catch (error) {
        const errStr = error.toString();
        logToSentry("post submit", {
            error,
            errStr,
        });

        yield put(actionCreators.postFailure(errStr));
    }
}

function* postSubmissionClear() {
    const { audits, auditUpload } = yield select((state) => state);
    const { cleanAllData } = sagaUtils;

    const auditId = audits.item.id;

    try {
        yield call(
            retrySaga,
            submitAllAuditAssignments,
            [auditId, auditUpload],
            3,
            2000
        );
    } catch (error) {
        console.error(`Final failure for audit ID ${auditId}:`, error);
        logToSentry(`Final failure for audit ID ${auditId}:`, { error });
        yield put(
            actions.toast.create.error(`Final failure for audit ID ${auditId}:`)
        );
        return; // Stop execution if retries fail
    }

    yield put(actionCreators.statusUploaded(0, false));
    yield call(cleanAllData, audits.item);
}

/** Status of upladed data */
function* statusInit() {
    try {
        let auditId;
        // Id is undefined in rare cases
        while (!auditId) {
            const { audits } = yield select((state) => state);
            auditId = audits?.item?.id;
            if (!auditId) yield delay(100); // ⏳ wait a bit before retry
        }

        const { audits, auditUpload } = yield select((state) => state);

        /* Get auditAssignment  */
        const auditAssignmentDB =
            (yield call(get, db.types.auditAssignment)) || [];

        const auditAssetDB = (yield call(get, db.types.auditAsset)) || [];

        const auditAssignments = db.queries.forAuditLocationAudit(
            audits.item.id,
            auditAssignmentDB
        );

        /* Get asetAuditors**/
        const assignmentIds = auditAssignments.map((item) => item.id);

        const assetAuditorsDB = (yield call(get, db.types.assetAuditor)) || [];
        const assetAuditors = db.queries.forAuditAssignments(
            assignmentIds,
            assetAuditorsDB
        );

        /** Current audit asset  */
        const auditAsset = auditAssetDB.filter(
            (item) => item.auditLocation.audit.id === audits.item.id
        );

        /* Get altAssets**/
        const altAssetsDB = (yield call(get, db.types.altAsset)) || [];
        const altAssets = db.queries.forAudit(audits.item.id, altAssetsDB);

        const uploadedAssetAuditors = assetAuditors.filter(
            (item) => item.assetSubmitted
        ).length;
        const uploadedAltAssets = altAssets.filter(
            (item) => item.assetSubmitted
        ).length;

        const addItems = () => {
            if (auditUpload[audits.item.id]) {
                const singatures = auditUpload[audits.item.id].auditSignature
                    ? { ...auditUpload[audits.item.id].auditSignature }
                    : auditUpload.auditSignatures;

                const firstAssetChecked = auditUpload[audits.item.id]
                    .firstAssetChecked
                    ? auditUpload[audits.item.id].firstAssetChecked
                    : null;

                return {
                    auditSignature: { ...singatures },
                    firstAssetChecked,
                };
            } else
                return {
                    auditSignature: { ...auditUpload.auditSignature },
                    firstAssetChecked: null,
                };
        };

        yield put(
            actionCreators.statusSet({
                ...auditUpload,
                [audits.item.id]: { ...addItems() },
                total: altAssets.length + auditAsset.length,
                uploaded: uploadedAssetAuditors + uploadedAltAssets,
                submissionUpload: 0,
            })
        );
    } catch (error) {
        const errStr = error.toString();

        logToSentry("statusInit", {
            error,
            errStr,
        });
        yield put(actions.toast.create.error("messages.auditUploadStatusInit"));
    }
}

function* updateRequest(actions) {
    try {
        const { auditUpload } = actions.payload;
        yield put(actionCreators.updateSuccess(auditUpload));
    } catch (error) {
        const errStr = error.toString();
        logToSentry("audit upload update request", {
            error,
            errStr,
        });
    }
}

function* clearSignature(actions) {
    try {
        const { groupAuditIds } = actions.payload;
        const reduxStateDB = (yield call(get, db.types.reduxState)) || [];

        // Shallow copy of auditUpload to avoid direct mutation
        const updatedAuditUpload = { ...reduxStateDB.auditUpload };

        // Remove specified audit IDs
        groupAuditIds.forEach((auditId) => {
            if (updatedAuditUpload[auditId]) {
                delete updatedAuditUpload[auditId];
            }
        });

        // Update Redux state with modified data
        const newReduxState = {
            ...reduxStateDB,
            auditUpload: updatedAuditUpload,
        };

        // Update redux state
        yield call(update, db.types.reduxState, () => newReduxState);
        yield put(actionCreators.clearSignatureSuccess(updatedAuditUpload));
    } catch (error) {
        const errStr = error.toString();
        console.error(error);
        logToSentry("audit upload clear signature", {
            error,
            errStr,
        });
        yield put(actionCreators.clearSignatureFailure(errStr));
    }
}

// For testing
export { updateRequest, post, statusInit, partialSubmit, clearSignature };
export default (function* () {
    yield all([
        takeLatest(actionTypes.AUDIT_UPLOAD_POST_REQUEST, post),
        takeLatest(
            actionTypes.AUDIT_UPLOAD_CLEAR_SIGNATURE_REQUEST,
            clearSignature
        ),
        takeLatest(
            actionTypes.AUDIT_PARTIAL_SUBMIT_POST_REQUEST,
            partialSubmit
        ),
        takeLatest(actionTypes.AUDIT_UPLOAD_STATUS_INIT, statusInit),
        takeLatest(actionTypes.AUDIT_UPLOAD_UPDATE_REQUEST, updateRequest),
        takeLatest(actionTypes.POST_SUBMISSION_CLEAR, postSubmissionClear),
        takeLatest(
            actionTypes.GROUP_AUDIT_SUBMISSION_CLEAR,
            groupAuditSubmissionClear
        ),
    ]);
})();

//TODO
// 1. Race codition needs to be addressed in some cases in statusInit
